import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
}) => {
  const location = useLocation();
  const { isAuthenticated, user, hasPermission, hasRole, requiresPasswordChange, requiresMFASetup } = useAuthStore();

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Requires password change - redirect to change password
  if (requiresPasswordChange && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Requires MFA setup - redirect to MFA setup
  if (requiresMFASetup && location.pathname !== '/mfa-setup' && location.pathname !== '/change-password') {
    return <Navigate to="/mfa-setup" replace />;
  }

  // Check required roles
  if (requiredRoles.length > 0 && !hasRole(...requiredRoles)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check required permissions
  if (requiredPermissions.length > 0) {
    const hasAllPermissions = requiredPermissions.every((permission) => hasPermission(permission));
    if (!hasAllPermissions) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;

